import React from "react"
import { Link } from "gatsby"
import GuideLayout from "../../components/guide/guide-layout"
import { findGuide } from "../../data/guides"

const guide = findGuide("phone-booth-or-meeting-pod")

const Page = () => (
  <GuideLayout guide={guide}>
    <p>
      The most common mistake with quiet space is buying one large pod and calling the problem solved. It fills up with two-person meetings, the people who needed to take a ten-minute call are still in the open, and the pod sits empty half the day because nobody wants to book it for a call. This guide is about matching the space to how your team actually works.
    </p>

    <h2>Count the calls, not the people</h2>
    <p>
      Start by watching a normal Tuesday. Count how many people leave their desk to take a call, hunt for an empty meeting room, or put on headphones and talk into the open. Most offices find that call-heavy roles (sales, recruitment, account management, support) generate several short calls per person per day, while other roles need a quiet spot a few times a week. Those two patterns need different things.
    </p>
    <p>
      For short calls, the unit of value is a one-person booth that is free right now. For meetings, it is a pod that can be booked for a fixed slot. Mixing them wastes both: a meeting in a phone booth is uncomfortable, and a call in a four-person pod blocks a room.
    </p>

    <h2>How many booths</h2>
    <p>
      There is no standard, but there is a range that works. Suppliers and workplace consultancies converge on roughly one one-person booth for every 10 to 15 desk-based people in a call-heavy team, and one for every 20 to 25 people in a team that mostly does focused work. If the office already has small meeting rooms, lean toward the lower end. If it has none, or the rooms are always booked, lean higher.
    </p>
    <table>
      <thead>
        <tr>
          <th>Team of</th>
          <th>Call-heavy roles</th>
          <th>Mostly focus work</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>20 people</td><td>2 booths</td><td>1 booth</td></tr>
        <tr><td>50 people</td><td>4 to 5 booths</td><td>2 to 3 booths</td></tr>
        <tr><td>100 people</td><td>8 to 10 booths</td><td>4 to 5 booths</td></tr>
      </tbody>
    </table>
    <p>
      Buy in stages if you can. Two booths placed well will show you within a month whether the team wants more, and where.
    </p>

    <h2>Booth or pod: the size decision</h2>
    <h3>One-person booth</h3>
    <p>
      About one square metre of floor. <Link to="/vibra-one/">Vibra One</Link> measures 1040 mm wide by 940 mm deep outside, with 896 mm by 880 mm inside and a standing-height table. It is built for calls, video meetings and an hour of focused work. Because it is small it can go where people already are, rather than where a room happens to be free.
    </p>
    <h3>Two- to four-person pod</h3>
    <p>
      From about 2 by 2 metres. This is for stand-ups, one-to-ones and hybrid meetings where two people in the room join a call. It replaces a small meeting room, so it should be booked like one. Our Vibra Four meeting pod is built to order for exactly this use, sized and configured for your room; <Link to="/contact-us/">tell us about the space</Link> and we will propose a layout.
    </p>

    <h2>Where to put them</h2>
    <ul>
      <li>
        <strong>Near the teams that make calls,</strong> not in a quiet corner they have to walk to. A booth 30 seconds away gets used; a booth two minutes away does not.
      </li>
      <li>
        <strong>Away from the desks that need silence,</strong> because doors open and close and people hover while they wait. Circulation areas, the edge of a kitchen or the end of a bank of desks work well.
      </li>
      <li>
        <strong>Not in the escape route.</strong> Check the fire plan, keep the required width to exits and update the fire risk assessment. Our <Link to="/guides/office-pod-planning-permission-uk/">guide to office pods and planning permission</Link> covers what else to check inside a leased building.
      </li>
      <li>
        <strong>Visible from a distance,</strong> so people can see whether it is occupied without walking over. Glass fronts help with this and with making the booth feel open rather than closed in.
      </li>
    </ul>

    <h2>Check before you order</h2>
    <ol>
      <li>
        <strong>Ceiling height.</strong> Vibra One is 2210 mm tall and needs a little clearance above for the ventilation to breathe. A 2400 mm ceiling is comfortable. Check for suspended ceilings, beams and lights along the delivery route too.
      </li>
      <li>
        <strong>Access.</strong> Measure the route from the loading bay to the final position, including lifts, door widths and the tightest turn, and send us the figures. We will confirm how the booth is delivered and assembled for your building.
      </li>
      <li>
        <strong>Power.</strong> One standard socket within reach of the booth. Vibra One runs on 100 to 240 volts and uses a motion sensor to switch light and fan on when someone enters, so it draws almost nothing when empty. A floor box or a wall socket near the booth avoids cable runs across walkways.
      </li>
      <li>
        <strong>Floor.</strong> A level surface. Adjustable feet take care of small differences; a raised access floor is fine as long as it is rated for the point loads, which any office floor is.
      </li>
      <li>
        <strong>Wi-Fi.</strong> Test the signal at the intended spot with the booth door closed. Dense walls attenuate Wi-Fi as well as sound, and a booth placed far from an access point can give a poor video call.
      </li>
    </ol>

    <h2>Buying, leasing and moving</h2>
    <p>
      A booth is furniture, so it moves with you. That is one of its advantages over a built meeting room, which is the landlord's the moment you leave. Vibra One can be dismantled and rebuilt in a new office in an afternoon. If your headcount is uncertain, ask us about staged delivery so the second and third booths arrive when you know where they should go.
    </p>
    <p className="note">Last reviewed September 2026.</p>
  </GuideLayout>
)

export default Page
